import { FileFolderAction, Id, Resource } from '@nextforce/api'
import { IconNode } from '@rneui/base'
import { Button, makeStyles, Text, useTheme } from '@rneui/themed'
import { t } from 'app/i18l'
import { Image } from 'expo-image'
import {
  useFileDetails,
  useFolderDetails,
} from 'file_folder_repository/queries/details'
import {
  getFileSize,
  getFriendlyMimeType,
  getPathName,
} from 'file_folder_repository/util'
import moment from 'moment'
import React, { useCallback, useEffect, useState } from 'react'
import { ActivityIndicator, ColorValue, View } from 'react-native'
import { PressableOpacity } from 'shared/components/pressable_opacity'
import { getDarkShadeColor } from 'shared/utils'
import { FileFolderIcon } from './file_folder_icon'

const THUMBNAIL_SIZE = 150

interface FileFolderDetailsViewProps {
  itemId: Id | undefined
  itemType: Resource['type'] | undefined
  onPressAction?(resource: Resource, action: string): void
  onPressThumbnail?(): void
  showActions?: boolean
  showPath?: boolean
  showOverlay?: boolean
  showMetadata?: boolean
}

export function FileFolderDetailsView({
  itemId,
  itemType,
  onPressAction,
  onPressThumbnail,
  showActions = true,
  showPath = true,
  showOverlay = false,
  showMetadata = true,
}: FileFolderDetailsViewProps) {
  const [imageError, setImageError] = useState(false)
  const [loadingImage, setLoadingImage] = useState(false)

  const fileDetailsQuery = useFileDetails(
    itemType === 'file' ? itemId : undefined,
    itemType === 'file'
  )
  const folderDetailsQuery = useFolderDetails(
    itemType === 'folder' ? itemId : undefined,
    itemType === 'folder'
  )

  const item =
    itemType === 'file' ? fileDetailsQuery.data : folderDetailsQuery.data

  const isLoading =
    itemType === 'file'
      ? fileDetailsQuery.isLoading
      : folderDetailsQuery.isLoading

  const error: any =
    itemType === 'file' ? fileDetailsQuery.error : folderDetailsQuery.error

  const onPressActionButton = useCallback(
    (action: string) => {
      if (!item || !onPressAction) {
        return
      }
      return onPressAction(item, action)
    },
    [onPressAction, item]
  )
  const styles = useStyles()

  useEffect(() => {
    if (itemId) {
      setImageError(false)
      setLoadingImage(false)
    }
  }, [itemId])

  if (isLoading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator
          size={'large'}
          color={styles.loadingContainer.color}
        />
      </View>
    )
  }
  if (!item) {
    return (
      <View style={styles.loadingContainer}>
        <Text>{t('errorLoadingDetails')}</Text>
        {!!error ? <Text>{error.message}</Text> : null}
      </View>
    )
  }
  const pathName = getPathName(item.path)

  return (
    <View style={styles.detailsContainer}>
      <View style={styles.iconDetailsContainer}>
        <PressableOpacity onPress={onPressThumbnail}>
          {item.type === 'file' &&
          item.currentVersion?.thumbnailUrl &&
          !imageError ? (
            <>
              <Image
                source={{
                  uri: item.currentVersion?.thumbnailUrl,
                }}
                style={{
                  width: THUMBNAIL_SIZE,
                  height: THUMBNAIL_SIZE,
                }}
                contentFit="contain"
                onError={() => setImageError(true)}
                onLoadStart={() => setLoadingImage(true)}
                onLoadEnd={() => setLoadingImage(false)}
              />
              {loadingImage ? (
                <ActivityIndicator
                  style={styles.loadingIndicator}
                  size="small"
                  color={styles.loadingIndicator.color}
                />
              ) : showOverlay ? (
                <View style={styles.overlayContainer}>
                  <Text style={styles.overlayText}>{t('view')}</Text>
                </View>
              ) : null}
            </>
          ) : (
            <View>
              <FileFolderIcon resource={item} size={100} style={styles.icon} />
              {showOverlay ? (
                <View style={styles.overlayContainer}>
                  <Text style={styles.overlayText}>{t('view')}</Text>
                </View>
              ) : null}
            </View>
          )}
        </PressableOpacity>
        <View
          style={[
            styles.itemDetailsContainer,
            showMetadata ? null : { justifyContent: 'center' },
          ]}
        >
          <Text style={[styles.title]}>{item.name}</Text>
          {item.type === 'file' && item.isPinned ? (
            <Text style={styles.text}>{t('pinned')}</Text>
          ) : null}
          <Text style={[styles.text]}>{item.description}</Text>
          {showMetadata ? (
            <>
              {item.type === 'folder' ? (
                <Text style={styles.text}>
                  {`${t('createdBy')} : ${item.createdBy?.firstName} ${
                    item.createdBy?.lastName
                  }`}
                </Text>
              ) : null}
              {item.type === 'file' ? (
                <>
                  <Text style={styles.text}>
                    {`${t('type')} : ${
                      item.currentVersion?.externalUrl
                        ? t('externalFile')
                        : getFriendlyMimeType(item.currentVersion?.fileMimeType)
                    }`}
                  </Text>
                  {!item.currentVersion?.externalUrl ? (
                    <Text style={styles.text}>
                      {`${t('fileSize')} : ${getFileSize(
                        item.currentVersion?.size ?? 0
                      )}`}
                    </Text>
                  ) : null}
                  <Text style={styles.text}>
                    {`${t('uploadedBy')} : ${
                      item.currentVersion?.uploadedBy.firstName
                    } ${item.currentVersion?.uploadedBy.lastName}`}
                  </Text>
                </>
              ) : null}
              <Text style={styles.text}>
                {`${t('uploadedOn')} : ${moment(item.created).format(
                  'MMM DD, YYYY'
                )}`}
              </Text>
              {pathName && showPath ? (
                <Text style={styles.text}>{`${t('path')} : ${pathName}`}</Text>
              ) : null}
            </>
          ) : null}
        </View>
      </View>
      {showActions ? (
        <ActionButtonList item={item} onPressAction={onPressActionButton} />
      ) : null}
    </View>
  )
}

const ActionButtonList = ({
  item,
  onPressAction,
}: {
  item: Resource
  onPressAction(action: string): void
}) => {
  const styles = useStyles()
  return (
    <View style={styles.actionButtonListContainer}>
      {item.actions?.map(action => (
        <ActionButton
          key={action}
          action={action}
          onPressAction={onPressAction}
        />
      )) ?? null}
    </View>
  )
}

const ActionButton = ({
  action,
  onPressAction,
}: {
  action: FileFolderAction
  onPressAction(action: string): void
}) => {
  const styles = useStyles()
  const { theme } = useTheme()
  const icon = getActionButtonIcon(
    action,
    action === 'delete' ? theme.colors.primary : theme.colors.black
  )
  const title = getActionButtonTitle(action)
  return (
    <Button
      type="clear"
      title={title}
      onPress={() => onPressAction(action)}
      buttonStyle={[styles.button]}
      titleStyle={styles.buttonTitle}
      icon={icon}
      iconContainerStyle={styles.actionButtonIconContainer}
      containerStyle={styles.actionButtonContainer}
    />
  )
}

const getActionButtonIcon = (
  action: FileFolderAction,
  color: ColorValue = 'white'
): IconNode | undefined => {
  switch (action) {
    case 'edit':
      return { name: 'edit', type: 'feather', color }
    case 'delete':
      return { name: 'trash', type: 'feather', color }
    case 'copy':
      return { name: 'copy', type: 'feather', color }
    case 'move':
      return { name: 'move', type: 'feather', color }
    case 'share':
      return { name: 'share', type: 'feather', color }
    case 'shareWithAck':
      return {
        name: 'account-eye-outline',
        type: 'material-design',
        color,
      }
  }
}

const getActionButtonTitle = (action: FileFolderAction): string | undefined => {
  switch (action) {
    case 'edit':
      return t('edit')
    case 'delete':
      return t('delete')
    case 'copy':
      return t('copy')
    case 'move':
      return t('move')
    case 'share':
      return t('share')
    case 'shareWithAck':
      return t('send')
  }
}

const useStyles = makeStyles(theme => ({
  detailsContainer: {
    paddingHorizontal: 20,
    paddingBottom: 10,
  },
  icon: {
    padding: 20,
    backgroundColor: theme.colors.white,
    borderRadius: 15,
    height: THUMBNAIL_SIZE,
    width: THUMBNAIL_SIZE,
  },
  title: {
    fontSize: 22,
    lineHeight: 22,
  },
  text: {
    marginTop: 5,
  },
  button: {
    backgroundColor: theme.colors.white,
    justifyContent: 'flex-start',
    height: 50,
  },
  bottomSheetBackground: {
    backgroundColor:
      theme.mode === 'dark' ? theme.colors.grey11 : theme.colors.grey6,
  },
  bottomSheetHandleIndicator: {
    backgroundColor:
      theme.mode === 'dark' ? theme.colors.black : theme.colors.grey4,
  },
  loadingContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    height: 100,
    color: theme.colors.primary,
  },
  iconDetailsContainer: {
    flexDirection: 'row',
  },
  itemDetailsContainer: {
    marginLeft: 20,
    flex: 1,
  },
  actionButtonListContainer: {
    marginTop: 20,
  },
  actionButtonIconContainer: { marginRight: 20 },
  actionButtonContainer: { borderRadius: 10, marginTop: 3 },
  buttonTitle: { color: theme.colors.black },
  loadingIndicator: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -10,
    marginLeft: -10,
    zIndex: 10,
    color: theme.colors.primary,
  },
  overlayContainer: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor:
      theme.mode === 'dark' ? getDarkShadeColor(0.6) : getDarkShadeColor(0.3),
  },
  overlayText: { fontWeight: 'bold', color: 'white', fontSize: 16 },
}))
